import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, FormInput, colors } from '@podiumhq/podium-ui';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import analytics from '@podiumhq/podium-analytica';
import { LOGIN_PAGE_EVENTS } from '@podiumhq/podium-analytica-events';
import { HeroElement } from '@podiumhq/first-meaningful-paint';

import {
  ButtonHolder,
  HelpText,
  Input,
  Logo,
  ToggleShow,
  ShowButton
} from './styles';
import { setCookies } from '../../lib/cookies';
import { isValidEmailOrPhone, formatIfPhone } from '../../lib/validation';
import { PASSWORD_LOGIN } from '../../graphql/mutations';
import { SSO_ENABLED } from '../../graphql/queries';
import { InvalidLoginBanner } from '..';
import logo from '../../images/Logo.png';

const LINK_STYLE = { textDecoration: 'none', color: colors.cobaltBlue };

const invalidUsernameText = 'Invalid email address';
const missingUsernameText = 'Email or mobile number is required';
const missingPasswordText = 'Password is required';
const invalidLoginText = usernameType =>
  `Incorrect ${usernameType} or password. Please try again.`;

export default function PasswordLogin({
  emailOrPhone,
  setEmailOrPhone,
  setRedirectUrl
}) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordInputVisible, setPasswordInputVisible] = useState(false);
  const [missingUsername, setMissingUsername] = useState(false);
  const [missingPassword, setMissingPassword] = useState(false);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [invalidLogin, setInvalidLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const client = useApolloClient();
  const [passwordLogin, { loading }] = useMutation(PASSWORD_LOGIN, {
    onError() {
      setInvalidLogin(true);
      setLoginLoading(false);
    },
    onCompleted(data) {
      const {
        passwordLogin: { idToken, refreshToken }
      } = data;

      setCookies({ idToken, refreshToken });
    }
  });

  async function getSSOEnabled() {
    setSsoLoading(true);
    const { data } = await client.query({
      query: SSO_ENABLED,
      variables: { emailOrPhone }
    });
    setSsoLoading(false);

    const {
      userSsoDetails: { ssoEnabled, redirectUrl }
    } = data;

    if (ssoEnabled) setCookies({ loggedInWithSso: true });

    if (ssoEnabled && passwordInputVisible) {
      analytics.track(LOGIN_PAGE_EVENTS.ssoLogin.onPasswordPage);
      setRedirectUrl(redirectUrl);
    } else if (ssoEnabled) {
      window.location.replace(redirectUrl);
    } else if (passwordInputVisible) {
      setLoginLoading(true);
      passwordLogin({
        variables: {
          input: { emailOrPhone: formatIfPhone(emailOrPhone), password }
        }
      });
    } else {
      setPasswordInputVisible(true);
    }
  }

  const onNext = () => {
    const isValidUsername = isValidEmailOrPhone(emailOrPhone);

    setMissingUsername(!emailOrPhone);
    setInvalidUsername(!isValidUsername);
    if (passwordInputVisible) setMissingPassword(!password);

    const noPasswordSSOCheck = !passwordInputVisible && isValidUsername;
    const withPasswordSSOCheck =
      passwordInputVisible && password && isValidUsername;

    if (noPasswordSSOCheck || withPasswordSSOCheck) getSSOEnabled();
  };

  const onEnter = e => {
    if (e.keyCode === 13) onNext();
  };

  const onPasswordKeydown = e => {
    setCapsLockOn(e.getModifierState('CapsLock'));
    if (e.keyCode === 13) onNext();
  };

  const onEmailOrPhoneChange = input => {
    if (missingUsername && input) {
      setMissingUsername(false);
      setInvalidUsername(false);
    }

    if (invalidUsername && isValidEmailOrPhone(input)) {
      setInvalidUsername(false);
    }

    if (invalidLogin) setInvalidLogin(false);

    setEmailOrPhone(input);
  };

  const onPasswordChange = input => {
    if (missingPassword && input) setMissingPassword(false);
    if (invalidLogin) setInvalidLogin(false);
    setPassword(input);
  };

  return (
    <HeroElement heroId="password-login">
      <Logo>
        <img src={logo} width="50%" alt="Podium Logo" />
      </Logo>
      <InvalidLoginBanner
        show={invalidLogin}
        isEmail={emailOrPhone.includes('@')}
        isPassword
        message={invalidLoginText(
          emailOrPhone.includes('@') ? 'email' : 'mobile number'
        )}
      />
      <Input>
        <FormInput
          autoFocus
          id="emailOrPhoneInput"
          errorMessage={
            (missingUsername && missingUsernameText) ||
            (invalidUsername && invalidUsernameText)
          }
          label="Email or mobile number"
          name="username"
          onChange={e => onEmailOrPhoneChange(e.target.value)}
          onFocus={() => setInvalidLogin(false)}
          onKeyDown={onEnter}
          tabindex="0"
          type="text"
          value={emailOrPhone}
        />
      </Input>
      {passwordInputVisible && (
        <Input>
          <FormInput
            autoFocus
            id="passwordInput"
            errorMessage={missingPassword && missingPasswordText}
            label="Password"
            onChange={e => onPasswordChange(e.target.value)}
            onFocus={() => setInvalidLogin(false)}
            onKeyDown={onPasswordKeydown}
            helpText={capsLockOn && 'Caps lock is on'}
            tabindex="0"
            type={showPassword ? 'text' : 'password'}
          />
          <ToggleShow>
            <ShowButton onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? 'Hide' : 'Show'}
            </ShowButton>
          </ToggleShow>
        </Input>
      )}
      <ButtonHolder>
        <Button
          fullwidth
          id="signInButton"
          size="large"
          onClick={onNext}
          disabled={ssoLoading}
          isLoading={loading || loginLoading}
          loadingText="Signing in..."
        >
          {passwordInputVisible ? 'Sign in' : 'Next'}
        </Button>
      </ButtonHolder>
      <HelpText>
        <Link id="forgotPasswordText" to="/request-code" style={LINK_STYLE}>
          Forgot password?
        </Link>
      </HelpText>
    </HeroElement>
  );
}
